
"use client"

import * as React from "react"
import { Check, ChevronsUpDown, PlusCircle } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export type ComboboxOption = { label: string; value: string };

type ComboboxProps = {
  options?: ComboboxOption[];
  /** Renders multiple sections with headings; search applies across all groups. */
  optionGroups?: { heading: string; options: ComboboxOption[] }[];
  value?: string;
  onChange?: (value: string) => void;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  createLabel?: string;
  disabled?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
};

export function Combobox({
  options,
  optionGroups,
  value,
  onChange,
  onValueChange,
  placeholder = "Select option...",
  searchPlaceholder,
  emptyMessage,
  createLabel = "Create new",
  disabled,
  triggerClassName,
  contentClassName,
}: ComboboxProps) {
  const handleChange = onChange ?? onValueChange ?? (() => {});
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("");

  const allOptions = React.useMemo(() => {
    if (optionGroups?.length) {
      return optionGroups.flatMap((g) => g.options);
    }
    return options ?? [];
  }, [optionGroups, options]);

  const getLabel = (val: string | undefined) => {
    if (!val) return placeholder;
    const option = allOptions.find((opt) => opt.value === val);
    return option ? option.label : val;
  }

  const handleSelect = (selectedValue: string) => {
    const newValue = selectedValue === value ? "" : selectedValue;
    handleChange(newValue);
    setInputValue("");
    setOpen(false);
  }
  
  const handleInputChange = (search: string) => {
    setInputValue(search);
  }

  const handleCreate = () => {
    handleChange(inputValue);
    setInputValue("");
    setOpen(false);
  }

  const matchesSearch = React.useCallback((option: ComboboxOption) => {
    if (!inputValue.trim()) return true;
    const searchWords = inputValue.toLowerCase().split(/\s+/).filter(Boolean);
    const optionLabelLower = option.label.toLowerCase();
    return searchWords.every((word) => optionLabelLower.includes(word));
  }, [inputValue]);

  const filteredOptions = React.useMemo(() => {
    if (optionGroups?.length) return [];
    return (options ?? []).filter(matchesSearch);
  }, [optionGroups, options, matchesSearch]);

  const filteredGroups = React.useMemo(() => {
    if (!optionGroups?.length) return null;
    return optionGroups
      .map((g) => ({
        heading: g.heading,
        options: g.options.filter(matchesSearch),
      }))
      .filter((g) => g.options.length > 0);
  }, [optionGroups, matchesSearch]);

  const displayValue = getLabel(value);

  const renderItem = (option: ComboboxOption, index: number) => (
    <CommandItem
      key={`${option.value}-${index}`}
      value={option.label}
      onSelect={() => {
        handleSelect(option.value);
      }}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4",
          value === option.value ? "opacity-100" : "opacity-0"
        )}
      />
      {option.label}
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", triggerClassName)}
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {displayValue}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-(--radix-popover-trigger-width) p-0", contentClassName)}
      >
        <Command>
          <CommandInput 
            placeholder={searchPlaceholder ?? "Search or create..."} 
            value={inputValue}
            onValueChange={handleInputChange}
          />
          <CommandList>
            <CommandEmpty>
                <div className="p-2">
                    <p className="text-sm text-center text-muted-foreground mb-2">{emptyMessage ?? "No results found."}</p>
                     {createLabel && inputValue && (
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={handleCreate}
                        >
                            <PlusCircle className="mr-2" />
                            {createLabel}: &quot;{inputValue}&quot;
                        </Button>
                    )}
                </div>
            </CommandEmpty>
            {filteredGroups ? (
              filteredGroups.map((group) => (
                <CommandGroup key={group.heading} heading={group.heading}>
                  {group.options.map((option, index) =>
                    renderItem(option, index),
                  )}
                </CommandGroup>
              ))
            ) : (
              <CommandGroup>
                {filteredOptions.map((option, index) => renderItem(option, index))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
